import React, { useEffect, useState } from "react";
import { Loader2, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

const FIELDS = [
  { key: "brand_email",      label: "Marka E-posta" },
  { key: "brand_phone",      label: "Telefon (display)" },
  { key: "brand_address",    label: "Adres", textarea: true },
  { key: "whatsapp_phone",   label: "WhatsApp Numarası (sadece rakam)", placeholder: "908508884865" },
  { key: "instagram_handle", label: "Instagram Handle (postunhazir)" },
  { key: "default_currency", label: "Varsayılan Para Birimi (TRY/USD/EUR)" },
  { key: "working_hours",    label: "Çalışma Saatleri" },
  { key: "google_maps_reviews_url", label: "Google Maps Yorumlar URL (reviews linki)", placeholder: "https://maps.app.goo.gl/..." },
];

export default function AdminSettingsPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data)).catch(() => toast.error("Yüklenemedi"));
  }, []);

  if (!data) return <div className="p-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>;

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <h1 className="font-display text-3xl font-bold mb-1">Site Ayarları</h1>
      <p className="text-sm text-ink/55 mb-8">İletişim bilgileri, döviz kurları ve global tercihler.</p>

      <div className="rounded-2xl bg-white border border-mist p-6 sm:p-8 max-w-3xl">

        {/* WhatsApp Visibility Toggle */}
        <div className="mb-6 p-5 rounded-xl border-2 border-dashed border-brand-pink/30 bg-brand-pink/5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">WhatsApp Butonu</h3>
              <p className="text-sm text-ink/60 mt-1">
                Sitede yüzen WhatsApp butonunu göster/gizle.
                {data.show_whatsapp ? (
                  <span className="ml-2 text-[#25D366] font-medium">● Şu an görünür</span>
                ) : (
                  <span className="ml-2 text-ink/40 font-medium">● Şu an gizli</span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => set("show_whatsapp", !data.show_whatsapp)}
              data-testid="settings-show-whatsapp-toggle"
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                data.show_whatsapp
                  ? "bg-[#25D366] text-white hover:bg-[#1FAB54]"
                  : "bg-ink/10 text-ink hover:bg-ink/20"
              }`}
            >
              {data.show_whatsapp ? (
                <><Eye className="h-4 w-4" /> Görünür</>
              ) : (
                <><EyeOff className="h-4 w-4" /> Gizli</>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="label-base">{f.label}</label>
              {f.textarea ? (
                <textarea rows={2} value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                  className="input-base resize-y" data-testid={`settings-${f.key}`} />
              ) : (
                <input type="text" value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder || ""} className="input-base" data-testid={`settings-${f.key}`} />
              )}
            </div>
          ))}

          <div className="border-t border-mist pt-5">
            <h3 className="font-display text-lg font-semibold mb-4">Döviz Kurları (1 TRY → X)</h3>
            <div className="grid grid-cols-3 gap-4">
              {["TRY","USD","EUR"].map((c) => (
                <div key={c}>
                  <label className="label-base">{c}</label>
                  <input type="number" step="0.0001" value={data.exchange_rates?.[c] ?? ""}
                    onChange={(e) => set("exchange_rates", { ...(data.exchange_rates || {}), [c]: Number(e.target.value) })}
                    className="input-base" data-testid={`settings-rate-${c}`} />
                </div>
              ))}
            </div>
            <label className="mt-4 inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!data.use_live_rates}
                onChange={(e) => set("use_live_rates", e.target.checked)}
                data-testid="settings-use-live-rates" />
              Canlı API'den kuru çek (exchangerate.host, 1 saatlik cache)
            </label>
          </div>
        </div>

        <button type="button" onClick={save} disabled={saving}
          data-testid="settings-save-btn"
          className="btn-gold mt-8 inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Kaydet
        </button>
      </div>
    </div>
  );
}
